<template>
  <div class="announcements-bell" v-click-outside="closeDropdown">
    <a href="#" class="bell-trigger" @click.prevent="toggleDropdown">
      <i class="tim-icons icon-bell-55"></i>
      <span v-if="unreadCount > 0" class="badge">
        {{ unreadCount > 99 ? '99+' : unreadCount }}
      </span>
    </a>

    <div v-if="isOpen" class="announcements-dropdown" :class="themeClass">
      <div class="dropdown-header">
        <span class="header-title">Announcements</span>
        <a
          v-if="unreadCount > 0"
          href="#"
          class="mark-all-read"
          @click.prevent="handleMarkAllRead"
        >
          Mark all read
        </a>
      </div>

      <div class="dropdown-content">
        <div v-if="loading" class="loading-state">
          <i class="fa fa-spinner fa-spin"></i>
          Loading...
        </div>

        <div v-else-if="announcements.length === 0" class="empty-state">
          No announcements
        </div>

        <div v-else class="announcements-list">
          <div
            v-for="announcement in announcements.slice(0, 5)"
            :key="announcement.id"
            class="announcement-item"
            :class="{ unread: !announcement.is_read }"
            @click="handleAnnouncementClick(announcement)"
          >
            <div class="priority-indicator" :class="'priority-' + announcement.priority"></div>
            <div class="announcement-content">
              <div class="announcement-title">{{ announcement.title }}</div>
              <div class="announcement-time">{{ formatTimeAgo(announcement.created_at) }}</div>
            </div>
          </div>
        </div>
      </div>

      <div class="dropdown-footer">
        <router-link to="/announcements/list" class="view-all" @click.native="closeDropdown">
          View all announcements
        </router-link>
      </div>
    </div>
  </div>
</template>

<script>
import announcementsApi from '@/services/announcementsApi';

export default {
  name: 'AnnouncementsBell',

  data() {
    return {
      isOpen: false,
      loading: false,
      unreadCount: 0,
      announcements: [],
      isLightMode: false
    };
  },

  computed: {
    themeClass() {
      return this.isLightMode ? 'theme-light' : 'theme-dark';
    }
  },

  mounted() {
    this.checkTheme();
    this.fetchUnreadCount();
  },

  methods: {
    // Theme detection
    checkTheme() {
      this.isLightMode = document.body.classList.contains('white-content');
    },

    // Dropdown interactions
    toggleDropdown() {
      this.checkTheme();
      this.isOpen = !this.isOpen;
      this.$emit('toggle', this.isOpen);
      if (this.isOpen) {
        this.fetchAnnouncements();
        this.fetchUnreadCount();
      }
    },

    closeDropdown() {
      this.isOpen = false;
      this.$emit('toggle', false);
    },

    async fetchUnreadCount() {
      try {
        const response = await announcementsApi.getUnreadCount();
        this.unreadCount = response.data.count || 0;
      } catch (error) {
        console.error('Failed to fetch unread count:', error);
      }
    },

    async fetchAnnouncements() {
      this.loading = true;
      try {
        const response = await announcementsApi.getAnnouncements({ limit: 5, sort: 'latest' });
        this.announcements = response.data.announcements || [];
      } catch (error) {
        console.error('Failed to fetch announcements:', error);
      } finally {
        this.loading = false;
      }
    },

    async handleMarkAllRead() {
      try {
        await announcementsApi.markAllAsRead();
        this.unreadCount = 0;
        this.announcements = this.announcements.map(a => ({ ...a, is_read: true }));
        this.$notify({
          type: 'success',
          message: 'All announcements marked as read',
          timeout: 3000
        });
      } catch (error) {
        console.error('Failed to mark all as read:', error);
        this.$notify({
          type: 'danger',
          message: 'Failed to mark announcements as read',
          timeout: 3000
        });
      }
    },

    async handleAnnouncementClick(announcement) {
      if (!announcement.is_read) {
        try {
          await announcementsApi.markAsRead(announcement.id);
          announcement.is_read = true;
          this.unreadCount = Math.max(0, this.unreadCount - 1);
        } catch (error) {
          console.error('Failed to mark as read:', error);
        }
      }
      this.closeDropdown();
      this.$router.push('/announcements/list');
    },

    formatTimeAgo(dateString) {
      const date = new Date(dateString);
      const now = new Date();
      const diffMs = now - date;
      const diffMins = Math.floor(diffMs / 60000);
      const diffHours = Math.floor(diffMs / 3600000);
      const diffDays = Math.floor(diffMs / 86400000);

      if (diffMins < 1) return 'Just now';
      if (diffMins < 60) return `${diffMins}m ago`;
      if (diffHours < 24) return `${diffHours}h ago`;
      if (diffDays < 7) return `${diffDays}d ago`;
      return date.toLocaleDateString();
    }
  },

  directives: {
    'click-outside': {
      bind(el, binding) {
        el._clickOutside = (event) => {
          if (!(el === event.target || el.contains(event.target))) {
            binding.value();
          }
        };
        document.addEventListener('click', el._clickOutside);
      },
      unbind(el) {
        document.removeEventListener('click', el._clickOutside);
      }
    }
  }
};
</script>

<style scoped>
.announcements-bell {
  position: relative;
  display: inline-block;
}

.bell-trigger {
  font-size: 18px;
  position: relative;
  padding: 0;
  transition: color 0.2s;
  color: inherit;
  display: flex;
  align-items: center;
}

.bell-trigger:hover {
  color: #e14eca;
}

.badge {
  position: absolute;
  top: -6px;
  right: -8px;
  min-width: 16px;
  height: 16px;
  border-radius: 8px;
  padding: 0 4px;
  background: #e14eca;
  color: #fff;
  font-size: 10px;
  font-weight: 700;
  line-height: 1;
  /* Flex centering eliminates the line-height/baseline drift that pushed the digit off-center */
  display: flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
}


.announcements-dropdown {
  position: absolute;
  top: 100%;
  right: 0;
  width: 320px;
  border-radius: 8px;
  z-index: 1000;
  margin-top: 0.5rem;
}

/* Dark mode (default) */
.announcements-dropdown.theme-dark {
  background: #22233a;
  border: 1px solid rgba(255, 255, 255, 0.1);
  box-shadow: 0 8px 40px rgba(0, 0, 0, 0.7), 0 2px 10px rgba(0, 0, 0, 0.5);
}

.theme-dark .dropdown-header {
  border-bottom: 1px solid rgba(255, 255, 255, 0.15);
}

.theme-dark .header-title {
  color: #fff;
}

.theme-dark .loading-state,
.theme-dark .empty-state {
  color: #adb5bd;
}

.theme-dark .announcement-item:hover {
  background: rgba(255, 255, 255, 0.1);
}

.theme-dark .announcement-item.unread {
  background: rgba(225, 78, 202, 0.2);
}

.theme-dark .announcement-item.unread:hover {
  background: rgba(225, 78, 202, 0.28);
}

.theme-dark .announcement-title {
  color: #fff;
}

.theme-dark .announcement-time {
  color: #adb5bd;
}

.theme-dark .dropdown-footer {
  border-top: 1px solid rgba(255, 255, 255, 0.15);
}

/* Light mode */
.announcements-dropdown.theme-light {
  background: #fff;
  border: 1px solid rgba(0, 0, 0, 0.15);
  box-shadow: 0 4px 25px rgba(0, 0, 0, 0.15);
}

.theme-light .dropdown-header {
  border-bottom: 1px solid rgba(0, 0, 0, 0.1);
}

.theme-light .header-title {
  color: #344675;
}

.theme-light .loading-state,
.theme-light .empty-state {
  color: #6c757d;
}

.theme-light .announcement-item:hover {
  background: rgba(0, 0, 0, 0.05);
}

.theme-light .announcement-item.unread {
  background: rgba(225, 78, 202, 0.1);
}

.theme-light .announcement-item.unread:hover {
  background: rgba(225, 78, 202, 0.15);
}

.theme-light .announcement-title {
  color: #344675;
}

.theme-light .announcement-time {
  color: #6c757d;
}

.theme-light .dropdown-footer {
  border-top: 1px solid rgba(0, 0, 0, 0.1);
}

/* Shared styles */
.dropdown-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
}

.header-title {
  font-weight: 600;
}

.mark-all-read {
  font-size: 0.8rem;
  color: #e14eca;
}

.mark-all-read:hover {
  color: #ba54f5;
  text-decoration: underline;
}

.dropdown-content {
  max-height: 300px;
  overflow-y: auto;
}

.loading-state,
.empty-state {
  padding: 2rem;
  text-align: center;
}

.announcements-list {
  padding: 0.5rem 0;
}

.announcement-item {
  display: flex;
  padding: 0.75rem 1rem;
  cursor: pointer;
  transition: background 0.2s;
}

.priority-indicator {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  margin-right: 0.75rem;
  margin-top: 0.4rem;
  flex-shrink: 0;
}

.priority-info {
  background: #17a2b8;
}

.priority-warning {
  background: #ffc107;
}

.priority-critical {
  background: #dc3545;
  animation: pulse 2s infinite;
}

.announcement-content {
  flex: 1;
  min-width: 0;
}

.announcement-title {
  font-size: 0.9rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.announcement-time {
  font-size: 0.75rem;
  margin-top: 0.25rem;
}

.dropdown-footer {
  padding: 0.75rem 1rem;
  text-align: center;
}

.view-all {
  color: #e14eca;
  font-size: 0.9rem;
}

.view-all:hover {
  color: #ba54f5;
  text-decoration: underline;
}
</style>
